import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import styles from "./CustomerDashboard.module.css";
import { CaretIcon, CopyIcon } from "../../../assets/icons";
import { convertToString, handleCopy } from "../../../utils";
import ButtonBadge from "../../../common/components/button-badge/ButtonBadge";
import { useLazyGetCustomerDashboardInformationQuery } from "../../../service/customer-service";
import { useDispatch } from "react-redux";
import { hideLoader, showLoader } from "../../../stores/slices/loader-slice";
import { BPNLOFFER } from "../../../model/customer/customer";
import { toast } from "react-toastify";
import AcceptOffer from "./bnploffer/AcceptOffer";
import { STORE_KEY } from "./constant";
import DepositComponent from "./payment/Deposit";
import AuthorizePaymentMethod from "./payment/authorizePaymentMethods";
import { useView } from "./provider/ViewContext";
import Repayment from "./payment/repayment";
import { updateUsername } from "../../../stores/slices/current-user-slice";
import { useLazyGetWalletEnquiryQuery } from "../../../service/virtual-wallet-service";
import { BounceLoader } from "react-spinners";
import VFDLogo from '../../../assets/icons/vfd_logo.png';

const CustomerDashboardComponent: React.FC = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const { currentView, setView, setOrder, resetContext } = useView();
	const [getDashboardDetails, { data }] = useLazyGetCustomerDashboardInformationQuery();
	const [getWalletInfo, { data: walletInfo, isLoading: fetchingWalletInfo, isFetching }] = useLazyGetWalletEnquiryQuery();
	const [storedBnpl, setStoredBnpl] = useState("");

	useEffect(() => {
		const bnplId = localStorage.getItem(STORE_KEY);
		if (bnplId) {
			setStoredBnpl(bnplId);
		}
		fetchDashboardDetails();
		resetContext();
		handleWalletEnquiry();
	}, []);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const status = params?.get("status");
		// if (status === AUTHORIZATION_STATUS.CARD_FAILURE || status === AUTHORIZATION_STATUS.CARD_SUCESS)
		if (status) {
			setOrder(localStorage?.getItem(STORE_KEY) ?? "");
			setView("authorizePayment");
		}
	}, [location]);

	const fetchDashboardDetails = async () => {
		dispatch(showLoader());
		await getDashboardDetails()
			.unwrap()
			.then(res => {
				setOrder(res?.bnplOfferId ?? "");
				dispatch(updateUsername({ userName: res?.fullName ?? "" }));
			})
			.catch(error => {
				toast.error(error?.data?.title);
			})
			.finally(() => {
				dispatch(hideLoader());
			});
	};



	const isNoActivePayment = useMemo(() => data?.bnplOffer === BPNLOFFER.NO_ACTIVE_PACKAGE || data?.bnplOffer === BPNLOFFER.NEW_PACKAGE_OFFER, [data]);
	const transactionInProgress = useMemo(() => data?.bnplOffer === BPNLOFFER.TRANSACTION_IN_PROGRESS, [data]);

	const handleAcceptOffer = () => {
		if (data?.bnplOfferId) {
			setView("acceptOffer");
			setOrder(data?.bnplOfferId);
			localStorage.setItem(STORE_KEY, data?.bnplOfferId ?? "");
		}
	};

	const handleBack = () => {
		setView("default");
		localStorage.removeItem(STORE_KEY);
	};

	const handleWalletEnquiry = () => {
		getWalletInfo()
			.unwrap()
			.then(res => {})
			.catch(err => toast.error(err?.data?.title as string));
	};

	return (
		<>
			{currentView === "repayment" && <Repayment />}
			{currentView === "authorizePayment" && <AuthorizePaymentMethod />}
			{currentView === "initialDeposit" && <DepositComponent />}
			{currentView === "acceptOffer" && <AcceptOffer handleBack={handleBack} BnplId={data?.bnplOfferId || storedBnpl} />}
			{currentView === "default" && (
				<div className={styles.dashboardContainer}>
					<div className={styles.cardContainer}>
						<div className={`${styles.card} ${styles.walletCard}`}>
							<div className={styles.cardHeader}>WALLET BALANCE</div>
							<div className={styles.cardBody}>
								<div className={styles.cardBodyContent}>
									<div className={styles.cardAmount}>
										<span className={styles.symbolCurrency}>₦</span>
										{walletInfo?.accountBalance ? walletInfo?.accountBalance.toLocaleString() : "N/A" }
										<span className={styles.textCurrency}> NGN</span>
									</div>
									<button disabled={fetchingWalletInfo} onClick={handleWalletEnquiry} className={styles.footerButton}>
										  {(fetchingWalletInfo || isFetching)? <BounceLoader size={15} /> : "Refresh Balance"}
									</button>
								</div>
							</div>
							<div className={styles.cardFooter}>
								<button disabled={fetchingWalletInfo} onClick={handleWalletEnquiry} className={styles.footerButton}>
									Refresh Balance {(fetchingWalletInfo || isFetching) && <BounceLoader size={15} />}
								</button>
							</div>
						</div>
						{walletInfo?.accountNo && (
							<div className={`${styles.card} ${styles.accountInfoCard}`}>
								<div className={styles.cardHeader}>
									<div title={"VFD Microfinance Bank"} className={styles.headContainer}>
										{"VFD Microfinance Bank"} <img className={styles.imageLogo} src={VFDLogo} alt="VFD"/>
									</div>
								</div>
								<div className={styles.cardBody}>
									<div className={styles.cardBodyContent}>
										{walletInfo?.accountNo}{" "}
										<span className={styles.copyIcon}>
											<CopyIcon onClick={() => handleCopy(walletInfo?.accountNo || "")} className={styles.CopyIcon} />
										</span>
									</div>
								</div>
								<div className={styles.cardFooter}>{walletInfo?.client}</div>
							</div>
						)}
						<div className={`${styles.card} ${styles.bnplStatusCard}`}>
							<div className={styles.cardHeader}>BNPL STATUS</div>
							<div className={styles.cardBody}>
								<div className={styles.cardBodyContent}>
									{data?.bnplOffer === BPNLOFFER.NEW_PACKAGE_OFFER && (
										<>
											<div className={styles.newOffer}> NEW PACKAGE OFFER </div>
											<ButtonBadge onClick={handleAcceptOffer} text="View BNPL Offer" />
										</>
									)}
									{data?.bnplOffer === BPNLOFFER.ACTIVE && (
										<>
											<div className={styles.active}>ACTIVE</div>
										</>
									)}
									{data?.bnplOffer === BPNLOFFER.NO_ACTIVE_PACKAGE && (
										<>
											<div className={styles.active}>NO ACTIVE PACKAGE</div>
											<ButtonBadge text="Purchase a product" />
										</>
									)}

									{data?.bnplOffer === BPNLOFFER.TRANSACTION_IN_PROGRESS && (
										<>
											<div className={styles.transactionInProgress}>TRANSACTION IN PROGRESS</div>
										</>
									)}
								</div>
							</div>
						</div>

						<div className={`${styles.card} ${styles.accountInfoCard}`}>
							<div className={styles.cardHeader}>
								<div className={styles.headContainer}>AMOUNT FINANCED</div>
							</div>
							<div className={styles.cardBody}>
								<div className={styles.cardBodyContent}>
									{isNoActivePayment ? (
										"No active Payment"
									) : (
										<>
											{" "}
											<span className={styles.symbolCurrency}>₦</span>
											{data?.amountFinanced?.toLocaleString()} <span className={styles.textCurrency}>NGN</span>
										</>
									)}
								</div>
							</div>
						</div>
						<div className={`${styles.card} ${styles.outstandingCard}`}>
							<div className={styles.cardHeader}>TOTAL OUTSTANDING </div>
							<div className={styles.cardBody}>
								<div className={styles.cardBodyContent}>
									<span className={styles.symbolCurrency}>₦</span>
									{data?.totalOutstanding?.toLocaleString()} <span className={styles.textCurrency}>NGN</span>
								</div>
							</div>
						</div>
						<div className={`${styles.card} ${styles.totalAmountOutstandingCard}`}>
							<div className={styles.cardHeader}>AMOUNT DUE</div>
							<div className={styles.cardBody}>
								<div className={styles.cardBodyContent}>
									{isNoActivePayment ? (
										"No active Payment"
									) : (
										<div>
											<span className={styles.symbolCurrency}>₦</span>
											{data?.amountDue?.toLocaleString()} <span className={styles.textCurrency}>NGN</span>
										</div>
									)}
									<ButtonBadge
										onClick={() => setView("repayment")}
										disabled={isNoActivePayment || transactionInProgress}
										text="Repay"
										icon={<CaretIcon className={styles.badgeIcon} />}
									/>
								</div>
							</div>
							<div className={styles.cardFooter}>
								<div className={styles.cardFooterContent}>
									{!isNoActivePayment && <span>DUE DATE {data?.paymentDueDate ? new Date(data?.paymentDueDate ?? "")?.toLocaleDateString() : ""}</span>}
									<ButtonBadge
										onClick={() => setView("repayment")}
										disabled={isNoActivePayment || transactionInProgress}
										text="Repay"
										icon={<CaretIcon className={styles.badgeIcon} />}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
};
export default CustomerDashboardComponent;
